import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { 
  CalendarIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const SetupCycle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const farmId = location.state?.farmId;
  const farmName = location.state?.farmName;
  const userId = location.state?.userId;

  // Wizard state - simplified to 2 steps
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Step 1: Produce Selection (simplified - direct search)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduce, setSelectedProduce] = useState(null);
  const [searching, setSearching] = useState(false);

  // Starting Stage
  const [startingStages, setStartingStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);

  // Planting System (new field from image 3)
  const [plantingSystems, setPlantingSystems] = useState([]);
  const [selectedPlantingSystem, setSelectedPlantingSystem] = useState(null);
  const [plantingSize, setPlantingSize] = useState('');

  // Step 2: Schedule
  const [cycleName, setCycleName] = useState('');
  const [dateStart, setDateStart] = useState(new Date().toISOString().split('T')[0]);
  const [dateEnd, setDateEnd] = useState('');
  const [expectedHarvest, setExpectedHarvest] = useState('');
  const [harvestUnit, setHarvestUnit] = useState('kg');

  // Set default end date on mount
  useEffect(() => {
    const start = new Date();
    const end = new Date(start.setMonth(start.getMonth() + 6));
    setDateEnd(end.toISOString().split('T')[0]);
  }, []);

  // Redirect if no farm data
  useEffect(() => {
    if (!farmId) {
      console.warn('No farm ID found, redirecting to farm setup');
      navigate('/setup/farm');
    }
  }, [farmId, navigate]);

  // Fetch starting stages and planting systems when produce is selected
  useEffect(() => {
    if (selectedProduce?.agro_category_id) {
      fetchStartingStages(selectedProduce.agro_category_id);
      fetchPlantingSystems(selectedProduce.agro_category_id);
    }
  }, [selectedProduce]);

  const fetchStartingStages = async (agroCatId) => {
    try {
      const { data, error } = await supabase
        .from('culture_type')
        .select('id, name, name_malay, icon')
        .eq('produce_agri_cat', agroCatId);
      
      if (error) throw error;
      setStartingStages(data || []);
    } catch (error) {
      console.error('Error fetching starting stages:', error);
      setStartingStages([]);
    }
  };

  const fetchPlantingSystems = async (agroCatId) => {
    try {
      const { data, error } = await supabase
        .from('planting_system_size_unit')
        .select('id, size_unit_name, size_unit_name_malay, agri_cat')
        .or(`agri_cat.eq.${agroCatId},agri_cat.is.null`)
        .order('id');
      
      if (error) throw error;
      setPlantingSystems(data || []);
    } catch (error) {
      console.error('Error fetching planting systems:', error);
      setPlantingSystems([]);
    }
  };

  // Global produce search (searches all categories)
  const searchProduce = useCallback(async (query) => {
    if (!query || query.length < 2) return;
    
    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('produce_view')
        .select('id, name_produce, name_produce_malay, icon, produce_main_type_name, agro_name, agro_category_id')
        .eq('active', true)
        .or(`name_produce.ilike.%${query}%,name_produce_malay.ilike.%${query}%,combined_produce_name.ilike.%${query}%`)
        .order('name_produce')
        .limit(15);
      
      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching produce:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchProduce(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchProduce]);

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!selectedProduce) newErrors.produce = 'Please select a produce';
        if (!selectedStage) newErrors.stage = 'Please select a starting stage';
        if (!selectedPlantingSystem) newErrors.plantingSystem = 'Please select a planting system';
        break;
      case 2:
        if (!cycleName.trim()) newErrors.cycleName = 'Cycle name is required';
        if (!dateStart) newErrors.dateStart = 'Start date is required';
        if (!dateEnd) newErrors.dateEnd = 'End date is required';
        if (dateStart && dateEnd && new Date(dateEnd) <= new Date(dateStart)) {
          newErrors.dateEnd = 'End date must be after start date';
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 2));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    setIsLoading(true);
    
    try {
      // Create season/cycle record
      const { data: seasonData, error: seasonError } = await supabase
        .from('season')
        .insert({
          farm: farmId,
          name_season: cycleName,
          produce_name: selectedProduce.id,
          culture_type: selectedStage,
          planting_system: selectedPlantingSystem,
          planting_system_size: plantingSize ? parseFloat(plantingSize) : null,
          date_start: dateStart,
          date_end: dateEnd,
          expected_harvest_amount: expectedHarvest ? parseFloat(expectedHarvest) : null,
          created_by: userId,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (seasonError) throw seasonError;
      console.log('✅ Season/Cycle created:', seasonData.id);

      // Update user's default season
      if (userId) {
        await supabase
          .from('user')
          .update({ season_default: seasonData.id })
          .eq('id', userId);
        
        await supabase
          .from('farm_user_role')
          .update({ default_season: seasonData.id })
          .eq('farm', farmId)
          .eq('user', userId);
      }

      console.log('✅ Registration complete! Redirecting to dashboard...');
      navigate('/dashboard', { 
        state: { 
          newUser: true,
          farmId: farmId,
          farmName: farmName,
          cycleName: cycleName
        }
      });
      
    } catch (error) {
      console.error('Cycle setup error:', error);
      setErrors({ submit: error.message || 'Failed to create cycle. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard', { 
      state: { 
        newUser: true,
        farmId: farmId,
        farmName: farmName
      }
    });
  };

  const clearProduceSelection = () => {
    setSelectedProduce(null);
    setSelectedStage(null);
    setSelectedPlantingSystem(null);
    setPlantingSize('');
    setSearchQuery('');
    setSearchResults([]);
    setStartingStages([]);
    setPlantingSystems([]);
  };

  // Get planting size label based on selected planting system
  const getPlantingSizeLabel = () => {
    if (!selectedPlantingSystem) return 'Planting Size';
    const system = plantingSystems.find(p => p.id === selectedPlantingSystem);
    return system ? `Planting Size (${system.size_unit_name})` : 'Planting Size';
  };

  // Render step indicators
  const renderStepIndicators = () => (
    <div className="flex items-center justify-center space-x-4 mb-6">
      {[
        { num: 1, label: 'Select Produce' },
        { num: 2, label: 'Schedule Crop' }
      ].map((step, idx) => (
        <React.Fragment key={step.num}>
          {idx > 0 && (
            <div className={`w-12 h-0.5 ${currentStep > idx ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          )}
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
              ${currentStep > step.num ? 'bg-green-500 text-white' : 
                currentStep === step.num ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-500'}`}>
              {currentStep > step.num ? '✓' : step.num}
            </div>
            <span className={`ml-2 text-sm hidden sm:inline
              ${currentStep >= step.num ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
              {step.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Select Produce (simplified - like Kapitani app image 2)
  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-800">What are you growing?</h3>
        <p className="text-sm text-gray-500 mt-1">Quick Select Variant</p>
      </div>
      
      {/* Search Produce - Global search */}
      <div>
        {errors.produce && <p className="text-red-500 text-xs mb-1">{errors.produce}</p>}
        
        {!selectedProduce ? (
          <>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search produce name..."
                className="input pl-10 pr-10"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            
            {searching && (
              <div className="flex justify-center py-4">
                <div className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full"></div>
              </div>
            )}
            
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-64 overflow-y-auto bg-white shadow-sm">
                {searchResults.map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSelectedProduce(item);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3"
                  >
                    {item.icon && (
                      <img src={item.icon} alt="" className="w-10 h-10 rounded-full object-cover bg-gray-100" 
                        onError={(e) => e.target.style.display = 'none'} />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">
                        {item.name_produce}
                        {item.name_produce_malay && ` (${item.name_produce_malay})`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.produce_main_type_name} | {item.agro_name}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-center text-gray-500 text-sm py-4">No produce found for "{searchQuery}"</p>
            )}
          </>
        ) : (
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              {selectedProduce.icon && (
                <img src={selectedProduce.icon} alt="" className="w-12 h-12 rounded-full object-cover bg-white"
                  onError={(e) => e.target.style.display = 'none'} />
              )}
              <div>
                <div className="font-semibold text-purple-800">{selectedProduce.name_produce}</div>
                <div className="text-xs text-purple-600">
                  {selectedProduce.produce_main_type_name} | {selectedProduce.agro_name}
                </div>
              </div>
            </div>
            <button
              onClick={clearProduceSelection}
              className="text-purple-600 hover:text-purple-800"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Starting Stage - Dropdown like in Kapitani */}
      {selectedProduce && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Starting Stage</label>
          {errors.stage && <p className="text-red-500 text-xs mb-1">{errors.stage}</p>}
          <select
            value={selectedStage || ''}
            onChange={(e) => setSelectedStage(parseInt(e.target.value))}
            className="input"
          >
            <option value="">Select starting stage...</option>
            {startingStages.map(stage => (
              <option key={stage.id} value={stage.id}>
                {stage.name} {stage.name_malay ? `(${stage.name_malay})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Planting System - Like Image 3 */}
      {selectedProduce && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Planting System</label>
          {errors.plantingSystem && <p className="text-red-500 text-xs mb-1">{errors.plantingSystem}</p>}
          <select
            value={selectedPlantingSystem || ''}
            onChange={(e) => {
              setSelectedPlantingSystem(parseInt(e.target.value));
              setPlantingSize(''); // Reset size when system changes
            }}
            className="input"
          >
            <option value="">Select planting system...</option>
            {plantingSystems.map(system => (
              <option key={system.id} value={system.id}>
                {system.size_unit_name} {system.size_unit_name_malay ? `(${system.size_unit_name_malay})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Planting Size - Label changes based on selected system (Image 4) */}
      {selectedPlantingSystem && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{getPlantingSizeLabel()}</label>
          <input
            type="number"
            value={plantingSize}
            onChange={(e) => setPlantingSize(e.target.value)}
            placeholder="e.g. 100"
            className="input"
            min="0"
            step="1"
          />
        </div>
      )}
    </div>
  );

  // Step 2: Schedule
  const renderStep2 = () => (
    <div className="space-y-5">
      <h3 className="text-xl font-semibold text-center text-gray-800">Schedule Crop</h3>
      
      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {errors.submit}
        </div>
      )}

      {/* Summary */}
      <div className="p-4 bg-gray-50 rounded-lg space-y-1">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Produce:</span> {selectedProduce?.name_produce}
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Category:</span> {selectedProduce?.produce_main_type_name} ({selectedProduce?.agro_name})
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Starting Stage:</span> {startingStages.find(s => s.id === selectedStage)?.name}
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Planting System:</span> {plantingSystems.find(p => p.id === selectedPlantingSystem)?.size_unit_name}
        </div>
        {plantingSize && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Planting Size:</span> {plantingSize}
          </div>
        )}
      </div>

      {/* Cycle Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Name *</label>
        {errors.cycleName && <p className="text-red-500 text-xs mb-1">{errors.cycleName}</p>}
        <input
          type="text"
          value={cycleName}
          onChange={(e) => setCycleName(e.target.value)}
          placeholder="e.g. Season 1 2026"
          className={`input ${errors.cycleName ? 'border-red-500' : ''}`}
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
          {errors.dateStart && <p className="text-red-500 text-xs mb-1">{errors.dateStart}</p>}
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className={`input pl-10 ${errors.dateStart ? 'border-red-500' : ''}`}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
          {errors.dateEnd && <p className="text-red-500 text-xs mb-1">{errors.dateEnd}</p>}
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className={`input pl-10 ${errors.dateEnd ? 'border-red-500' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* Expected Harvest */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Expected Harvest (optional)</label>
        <div className="flex gap-2">
          <input
            type="number"
            value={expectedHarvest}
            onChange={(e) => setExpectedHarvest(e.target.value)}
            placeholder="e.g. 1000"
            className="input flex-1"
            min="0"
            step="0.01"
          />
          <select
            value={harvestUnit}
            onChange={(e) => setHarvestUnit(e.target.value)}
            className="input w-24"
          >
            <option value="kg">kg</option>
            <option value="ton">ton</option>
            <option value="unit">unit</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/logo-Kapitani.png" alt="Logo" className="h-12 w-12 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-gray-900">Add Produce</h2>
          <p className="text-sm text-gray-600">
            {farmName || 'Your Farm'} - Set up your first farming cycle
          </p>
        </div>

        {/* Main Progress */}
        <div className="flex items-center justify-center mb-4 space-x-2 text-xs">
          <span className="text-green-600">✓ Account</span>
          <span className="text-gray-400">—</span>
          <span className="text-green-600">✓ Farm</span>
          <span className="text-gray-400">—</span>
          <span className="text-purple-600 font-medium">● Cycle</span>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Step Indicators */}
          {renderStepIndicators()}

          {/* Step Content */}
          <div className="min-h-[350px]">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-6 pt-4 border-t">
            <button
              onClick={currentStep === 1 ? handleSkip : handleBack}
              className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              {currentStep === 1 ? (
                'Skip for now'
              ) : (
                <>
                  <ChevronLeftIcon className="h-4 w-4" /> Back
                </>
              )}
            </button>
            
            {currentStep < 2 ? (
              <button
                onClick={handleNext}
                disabled={!selectedProduce || !selectedStage || !selectedPlantingSystem}
                className="flex items-center gap-1 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Schedule crop <ChevronRightIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-4">
          © 2026 Kapitani. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default SetupCycle;
